/**
 * HelpPanel — el centro de ayuda dentro de la app.
 *
 * Pattern: DESIGN_SYSTEM.md → Help Panel Pattern (aprobado 2026-09-12).
 *
 * ── Por qué no es AssistantShell ─────────────────────────────────────────────
 *
 * Es el vecino más cercano y la tentación es evidente, pero resuelve otra cosa:
 * una conversación (entrada de texto, mensajes que se acumulan, pills). Acá hay
 * lectura: una lista, un artículo, un buscador. Comparten el lugar de la
 * pantalla y el gesto de abrir y cerrar; no comparten el adentro.
 *
 * ── Las tres decisiones de Eduardo, hechas código ────────────────────────────
 *
 *  1. Convive con el dock de Evo Brain en el mismo borde, y **uno reemplaza al
 *     otro**: abrir la ayuda cierra el asistente. Dos paneles anclados al mismo
 *     lado, abiertos a la vez, se pelean por la pantalla.
 *  2. También se entra desde Ajustes, como complemento.
 *  3. Los artículos de módulos apagados no se muestran — y eso lo filtra el
 *     SERVIDOR, no este componente: en una cuenta cliente, listar funciones que
 *     no compró es peor que ruido.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../Icon.js';
import { IconButton } from './IconButton.js';
import { Button } from './Button.js';
import { LoadingState } from './LoadingState.js';
import { Input } from './Input.js';
import { ListItem } from './ListItem.js';
import { EmptyState } from './EmptyState.js';
import { useArticulosDeAyuda, useArticuloDeAyuda } from '../../hooks/useAyuda.js';

interface Props {
  abierto: boolean;
  onCerrar: () => void;
  /** El módulo de la pantalla donde está parado el usuario, si la ruta es de
   *  un módulo. Decide qué artículos salen arriba de todo. */
  moduloActual: string | null;
  /** «Ver el recorrido otra vez», si esta pantalla tiene uno. */
  onVerRecorrido?: (() => void) | undefined;
}

const SECCION: React.CSSProperties = {
  fontSize: 'var(--evo-fs-tiny)',
  color: 'var(--evo-fg-4)',
  fontFamily: 'var(--evo-font)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  padding: 'var(--evo-space-4) var(--evo-space-4) var(--evo-space-2)',
};

export function HelpPanel({ abierto, onCerrar, moduloActual, onVerRecorrido }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [busqueda, setBusqueda] = useState('');
  const [abiertoId, setAbiertoId] = useState<string | null>(null);

  const { data, isLoading } = useArticulosDeAyuda(abierto);
  const { data: articulo } = useArticuloDeAyuda(abiertoId);

  // Al cerrar y volver a abrir se vuelve al listado: dejar abierto el artículo
  // de la vez pasada es empezar donde nadie pidió empezar.
  useEffect(() => { if (!abierto) { setAbiertoId(null); setBusqueda(''); } }, [abierto]);

  useEffect(() => {
    if (!abierto) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCerrar(); };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); };
  }, [abierto, onCerrar]);

  const todos = useMemo(() => data?.articulos ?? [], [data]);

  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (q === '') return null;
    return todos.filter(a =>
      a.titulo.toLowerCase().includes(q) || a.resumen.toLowerCase().includes(q));
  }, [busqueda, todos]);

  // Lo de ESTA pantalla primero. Es la regla 1 del pattern: un buscador vacío
  // como primera vista convierte la ayuda en un manual — obliga a saber qué
  // preguntar antes de recibir nada.
  const deEstaPantalla = useMemo(
    () => (moduloActual ? todos.filter(a => a.modulo === moduloActual) : []),
    [moduloActual, todos],
  );
  const resto = useMemo(
    () => todos.filter(a => !deEstaPantalla.includes(a)),
    [todos, deEstaPantalla],
  );

  if (!abierto) return null;

  // ListItem y no un <button> a mano: es el primitive de fila, y el pattern
  // manda usar el List Pattern que ya existe (REGLA #3).
  const fila = (a: { id: string; titulo: string; resumen: string }) => (
    <ListItem
      key={a.id}
      itemId={a.id}
      title={a.titulo}
      subtitle={a.resumen}
      density="compact"
      onClick={() => setAbiertoId(a.id)}
    />
  );

  return (
    <aside
      style={{
        width: 'var(--evo-help-panel-w)',
        flexShrink: 0,
        borderLeft: '1px solid var(--evo-stroke)',
        background: 'var(--evo-bg-2)',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
      aria-label={t('ayuda.titulo')}
    >
      <header style={{
        display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)',
        padding: 'var(--evo-space-3) var(--evo-space-4)',
        borderBottom: '1px solid var(--evo-stroke)', flexShrink: 0,
      }}>
        {abiertoId !== null && (
          <IconButton
            icon={<Icon name="chevron-left" size={14} />}
            ariaLabel={t('common.back')}
            variant="ghost"
            size="sm"
            onClick={() => setAbiertoId(null)}
          />
        )}
        <span style={{ flex: 1, fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-1)', fontFamily: 'var(--evo-font)' }}>
          {abiertoId !== null ? (articulo?.titulo ?? t('ayuda.titulo')) : t('ayuda.titulo')}
        </span>
        <IconButton
          icon={<Icon name="close" size={14} />}
          ariaLabel={t('common.close')}
          variant="ghost"
          size="sm"
          onClick={onCerrar}
        />
      </header>

      {abiertoId !== null ? (
        <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--evo-space-4)' }}>
          {!articulo && <LoadingState size="sm" />}
          {articulo && (
            <div className="evo-doc" style={{ fontSize: 'var(--evo-fs-body-lg)', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)', lineHeight: 1.6 }}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{articulo.cuerpo}</ReactMarkdown>
            </div>
          )}
          {articulo?.ruta && (
            <div style={{ marginTop: 'var(--evo-space-4)' }}>
              <Button
                variant="outline"
                tone="cyan"
                size="sm"
                onClick={() => { navigate(articulo.ruta!); onCerrar(); }}
              >
                {t('ayuda.irALaPantalla')}
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <div style={{ padding: 'var(--evo-space-3) var(--evo-space-4)', borderBottom: '1px solid var(--evo-stroke)' }}>
            <Input
              value={busqueda}
              onChange={setBusqueda}
              type="search"
              size="sm"
              placeholder={t('ayuda.buscar')}
              iconBefore={<Icon name="search" size={13} />}
              fullWidth
            />
          </div>

          {isLoading && <LoadingState size="sm" />}

          {!isLoading && filtrados !== null && (
            filtrados.length === 0
              ? <EmptyState title={t('ayuda.sinResultados')} />
              : <div>{filtrados.map(fila)}</div>
          )}

          {!isLoading && filtrados === null && (
            <>
              {deEstaPantalla.length > 0 && (
                <>
                  <div style={SECCION}>{t('ayuda.enEstaPantalla')}</div>
                  {deEstaPantalla.map(fila)}
                </>
              )}

              {onVerRecorrido && (
                <div style={{ padding: 'var(--evo-space-3) var(--evo-space-4)', borderBottom: '1px solid var(--evo-stroke)' }}>
                  <Button variant="ghost" tone="cyan" size="sm" onClick={() => { onVerRecorrido(); onCerrar(); }}>
                    {t('recorrido.volverAVer')}
                  </Button>
                </div>
              )}

              {resto.length > 0 && (
                <>
                  <div style={SECCION}>{t('ayuda.todoLoDemas')}</div>
                  {resto.map(fila)}
                </>
              )}
            </>
          )}
        </div>
      )}
    </aside>
  );
}
